import { useNavigate } from "react-router-dom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Onboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const handleRoleSelection = async (role: string) => {
    try {
      console.log("Selected role:", role); // Debug log
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "No user found. Please log in again.",
        });
        navigate("/login");
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          role: role.toLowerCase(),
        })
        .eq('id', user.id);

      if (error) throw error;

      console.log("Profile updated successfully"); // Debug log

      toast({
        title: "Welcome!",
        description: `You've joined as a ${role}`,
      });

      // Navigate based on role
      if (role.toLowerCase() === 'learner') {
        console.log("Navigating to learner home"); // Debug log
        navigate("/home");
      } else {
        console.log("Navigating to investor home"); // Debug log
        navigate("/investor/home");
      }
    } catch (error) {
      console.error("Error setting role:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to set your role. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Welcome! Let's get started</CardTitle>
          <CardDescription>
            Choose how you want to use the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup
            className="grid gap-4"
            onValueChange={handleRoleSelection}
          >
            <div>
              <RadioGroupItem
                value="learner"
                id="learner"
                className="peer sr-only"
              />
              <Label
                htmlFor="learner"
                className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
              >
                <span className="text-lg font-semibold">Start as a Learner</span>
                <span className="text-sm text-muted-foreground">
                  Learn from experienced investors
                </span>
              </Label>
            </div>
            <div>
              <RadioGroupItem
                value="investor"
                id="investor"
                className="peer sr-only"
              />
              <Label
                htmlFor="investor"
                className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
              >
                <span className="text-lg font-semibold">Start as an Investor</span>
                <span className="text-sm text-muted-foreground">
                  Share your investment knowledge
                </span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;